// =============================================================================
// charts — pure-JS chart → SVG generator (isomorphic: client + server, no deps)
// =============================================================================
// chartToSvg(spec) returns a complete <svg> string for bar / line / pie / donut.
// Used inline in the UI report, and drawn into PDFs via svg-to-pdfkit.
// Kept dependency-free and framework-agnostic so any feature can reuse it.
// =============================================================================

export interface ChartSeries {
  name?: string;
  data: number[];
}
export interface ChartSpec {
  type: 'bar' | 'line' | 'pie' | 'donut';
  title?: string;
  labels: string[];
  series: ChartSeries[];
  width?: number;
  height?: number;
}

const PALETTE = ['#1e3a5f', '#b8860b', '#2d6a4f', '#c53030', '#378add', '#993556', '#639922', '#5a3a16'];
const esc = (s: unknown) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const FONT = "font-family='Georgia,\"Times New Roman\",serif'";

function niceMax(v: number): number {
  if (!isFinite(v) || v <= 0) return 1;
  const p = Math.pow(10, Math.floor(Math.log10(v)));
  const n = v / p;
  const m = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10;
  return m * p;
}
const fmt = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(n < 1 ? 2 : 1));

export function chartToSvg(spec: ChartSpec): string {
  const W = Math.max(220, Math.round(spec.width ?? 640));
  const H = Math.max(160, Math.round(spec.height ?? 340));
  const series = (spec.series ?? []).filter((s) => Array.isArray(s.data));
  if (!series.length) return `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="40"></svg>`;
  if (spec.type === 'pie' || spec.type === 'donut') return pie(spec, series, W, H);
  return axis(spec, series, W, H);
}

function title(spec: ChartSpec, W: number): string {
  return spec.title ? `<text x="${W / 2}" y="20" text-anchor="middle" ${FONT} font-size="15" font-weight="700" fill="#1c170f">${esc(spec.title)}</text>` : '';
}

function legendRow(series: ChartSeries[], W: number, y: number): string {
  const items = series.map((s, i) => ({ name: s.name ?? `Series ${i + 1}`, color: PALETTE[i % PALETTE.length] }));
  if (items.length < 2) return '';
  const gap = 14;
  const widths = items.map((it) => 16 + it.name.length * 6.6);
  const total = widths.reduce((a, b) => a + b, 0) + gap * (items.length - 1);
  let x = (W - total) / 2;
  let out = '';
  items.forEach((it, i) => {
    out += `<rect x="${x}" y="${y - 8}" width="10" height="10" rx="2" fill="${it.color}"/>`;
    out += `<text x="${x + 14}" y="${y}" ${FONT} font-size="11" fill="#444">${esc(it.name)}</text>`;
    x += widths[i] + gap;
  });
  return out;
}

// ── bar / line (shared axes) ─────────────────────────────────────────────────
function axis(spec: ChartSpec, series: ChartSeries[], W: number, H: number): string {
  const labels = spec.labels ?? [];
  const n = Math.max(labels.length, ...series.map((s) => s.data.length));
  const hasLegend = series.length >= 2;
  const mTop = spec.title ? 34 : 14;
  const mBottom = 36 + (hasLegend ? 18 : 0);
  const mLeft = 46;
  const mRight = 14;
  const pw = W - mLeft - mRight;
  const ph = H - mTop - mBottom;
  const x0 = mLeft;
  const y0 = mTop + ph;

  const maxVal = Math.max(1, ...series.flatMap((s) => s.data.map((v) => (isFinite(v) ? v : 0))));
  const yMax = niceMax(maxVal);
  const yOf = (v: number) => y0 - (v / yMax) * ph;
  const xc = (i: number) => x0 + (pw / n) * (i + 0.5);

  let g = '';
  // y gridlines + labels
  const ticks = 4;
  for (let t = 0; t <= ticks; t++) {
    const val = (yMax / ticks) * t;
    const y = yOf(val);
    g += `<line x1="${x0}" y1="${y.toFixed(1)}" x2="${x0 + pw}" y2="${y.toFixed(1)}" stroke="#e3ddcf" stroke-width="1"/>`;
    g += `<text x="${x0 - 6}" y="${(y + 3).toFixed(1)}" text-anchor="end" ${FONT} font-size="10" fill="#888">${fmt(val)}</text>`;
  }
  // x labels
  for (let i = 0; i < n; i++) {
    const lab = labels[i] ?? '';
    g += `<text x="${xc(i).toFixed(1)}" y="${y0 + 16}" text-anchor="middle" ${FONT} font-size="10" fill="#555">${esc(lab.length > 14 ? lab.slice(0, 13) + '…' : lab)}</text>`;
  }

  if (spec.type === 'line') {
    series.forEach((s, si) => {
      const col = PALETTE[si % PALETTE.length];
      const pts = s.data.map((v, i) => `${xc(i).toFixed(1)},${yOf(isFinite(v) ? v : 0).toFixed(1)}`).join(' ');
      g += `<polyline points="${pts}" fill="none" stroke="${col}" stroke-width="2.5" stroke-linejoin="round" stroke-linecap="round"/>`;
      s.data.forEach((v, i) => {
        g += `<circle cx="${xc(i).toFixed(1)}" cy="${yOf(isFinite(v) ? v : 0).toFixed(1)}" r="3" fill="${col}"/>`;
      });
    });
  } else {
    // bar (grouped)
    const groupW = (pw / n) * 0.7;
    const bw = groupW / series.length;
    series.forEach((s, si) => {
      const col = PALETTE[si % PALETTE.length];
      s.data.forEach((v, i) => {
        const val = isFinite(v) ? v : 0;
        const bx = xc(i) - groupW / 2 + si * bw;
        const by = yOf(val);
        const bh = y0 - by;
        g += `<rect x="${bx.toFixed(1)}" y="${by.toFixed(1)}" width="${(bw - 2).toFixed(1)}" height="${Math.max(0, bh).toFixed(1)}" fill="${col}" rx="1.5"/>`;
        if (series.length === 1) g += `<text x="${xc(i).toFixed(1)}" y="${(by - 4).toFixed(1)}" text-anchor="middle" ${FONT} font-size="10" fill="#333">${fmt(val)}</text>`;
      });
    });
  }

  // axes
  g += `<line x1="${x0}" y1="${y0}" x2="${x0 + pw}" y2="${y0}" stroke="#9a8c70" stroke-width="1"/>`;
  g += `<line x1="${x0}" y1="${mTop}" x2="${x0}" y2="${y0}" stroke="#9a8c70" stroke-width="1"/>`;

  const leg = hasLegend ? legendRow(series, W, H - 6) : '';
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}">${title(spec, W)}${g}${leg}</svg>`;
}

// ── pie / donut ──────────────────────────────────────────────────────────────
function pie(spec: ChartSpec, series: ChartSeries[], W: number, H: number): string {
  const data = series[0].data.map((v) => (isFinite(v) && v > 0 ? v : 0));
  const labels = spec.labels ?? [];
  const sum = data.reduce((a, b) => a + b, 0) || 1;
  const mTop = spec.title ? 30 : 8;
  const cx = H * 0.5 + 4;
  const cy = mTop + (H - mTop - 8) / 2;
  const r = Math.min(cx - 8, (H - mTop - 16) / 2);
  const inner = spec.type === 'donut' ? r * 0.55 : 0;

  let ang = -Math.PI / 2;
  let slices = '';
  const legend: string[] = [];
  data.forEach((v, i) => {
    const frac = v / sum;
    const a2 = ang + frac * Math.PI * 2;
    const col = PALETTE[i % PALETTE.length];
    const x1 = cx + r * Math.cos(ang), y1 = cy + r * Math.sin(ang);
    const x2 = cx + r * Math.cos(a2), y2 = cy + r * Math.sin(a2);
    const large = frac > 0.5 ? 1 : 0;
    if (frac > 0) {
      if (inner > 0) {
        const ix1 = cx + inner * Math.cos(a2), iy1 = cy + inner * Math.sin(a2);
        const ix2 = cx + inner * Math.cos(ang), iy2 = cy + inner * Math.sin(ang);
        slices += `<path d="M${x1.toFixed(1)},${y1.toFixed(1)} A${r},${r} 0 ${large} 1 ${x2.toFixed(1)},${y2.toFixed(1)} L${ix1.toFixed(1)},${iy1.toFixed(1)} A${inner},${inner} 0 ${large} 0 ${ix2.toFixed(1)},${iy2.toFixed(1)} Z" fill="${col}"/>`;
      } else {
        slices += `<path d="M${cx},${cy} L${x1.toFixed(1)},${y1.toFixed(1)} A${r},${r} 0 ${large} 1 ${x2.toFixed(1)},${y2.toFixed(1)} Z" fill="${col}"/>`;
      }
    }
    const pct = Math.round(frac * 100);
    legend.push(`${esc(labels[i] ?? `#${i + 1}`)} — ${fmt(v)} (${pct}%)`);
    ang = a2;
  });

  // legend on the right
  const lx = cx + r + 16;
  let leg = '';
  legend.forEach((t, i) => {
    const y = mTop + 14 + i * 18;
    leg += `<rect x="${lx}" y="${y - 9}" width="10" height="10" rx="2" fill="${PALETTE[i % PALETTE.length]}"/>`;
    leg += `<text x="${lx + 15}" y="${y}" ${FONT} font-size="11" fill="#444">${t}</text>`;
  });

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}">${title(spec, W)}${slices}${leg}</svg>`;
}
